import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react'
import {renderLayoutsSwitch} from '../../utils/contentRenderer'

// Helper function to render area content - moved outside component for better performance
const renderAreaContent = (areaContent, areaName, props) => {
    if (!Array.isArray(areaContent)) return null

    return areaContent.map((item, index) => {
        if (!item?.ContentLink?.Expanded) return null

        return (
            <Box key={`${areaName}-${index}`}>
                {renderLayoutsSwitch(item.ContentLink.Expanded, props)}
            </Box>
        )
    })
}

/**
 * Generic component to render any area from Optimizely CMS content
 * Supports any page type (ProductLandingPage, WebContentPage, etc.) and any area property
 *
 * @param {Object} content - The CMS content object (e.g., ProductLandingPage, WebContentPage)
 * @param {string} area - The area property name to render (e.g., 'AboveResultArea', 'TopArea', 'MainArea')
 * @param {string} className - Optional CSS class for the container
 * @param {Object} containerProps - Additional props to pass to the container Box
 */
const OptimizelyArea = ({content, area, className, ...props}) => {
    // Direct property access with memoization for performance
    const areaContent = useMemo(() => {
        if (!content || !area) return null
        return content[area] || null
    }, [content, area])

    // Early return if no content
    if (!areaContent) return null

    return (
        <Box className={className} {...props?.containerProps}>
            {renderAreaContent(areaContent, area, props)}
        </Box>
    )
}

OptimizelyArea.displayName = 'OptimizelyArea'

OptimizelyArea.propTypes = {
    /** The CMS content object from Optimizely */
    content: PropTypes.object.isRequired,
    /** The area property name to render from the content object */
    area: PropTypes.string.isRequired,
    /** Optional CSS class name */
    className: PropTypes.string,
    /** Additional container props are spread to the Box component */
}

OptimizelyArea.defaultProps = {
    className: undefined
}

export default OptimizelyArea